/**
 * Start a Quixo Audit from the portal (pending item 13): the website to audit plus optional named competitor
 * websites. Posts to POST /v1/growth-audit/jobs (qm-api forwards it to qm-audit POST /v1/pipeline/jobs) with the entitlement from the link
 * (`/audit/new?entitlement=<id>&tier=<tier>`). The server owns validation and the tier limit (Essential 3,
 * Pro/Enterprise 5) and fills the remaining places with suggestions; the form mirrors the limit and maps the
 * server's { field, index, reason } back to the input. Named competitors ship behind AUDIT_PORTAL_COMPETITORS (off
 * by default): while off, the server answers feature_disabled and the form says so on the competitor group.
 *
 * Connected accounts (pending item 21, opt-in): the form lists the workspace's connected accounts
 * (GET /v1/connectors/connections, connected only) for the eight audit providers and sends at most one
 * `{ provider, connectionId }` per provider as `connectedAccounts`. Ids only: no token ever reaches the browser or
 * qm-audit. The server checks every id belongs to this workspace (404 otherwise) and the report adds an
 * information-only "Connected data" section; it never changes the score.
 */
import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { AUDIT_CONNECTED_PROVIDERS, type AuditConnectedProvider } from '@qm/contracts/connectors';
import { Button, EmptyState, ErrorState, Field, Heading, Loading, Panel, Select, Text, TextInput } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff, BffError } from '../../../lib/bff';
import { useResource } from '../../../lib/resource';

export const competitorLimitFor = (tier: string | null): 3 | 4 | 5 =>
  tier === 'essential' ? 3 : tier === 'pro' || tier === 'professional' ? 4 : 5;

interface JobBody {
  entitlementId: string; targetUrl: string; locale: 'en' | 'ar'; competitors?: string[];
  connectedAccounts?: { provider: AuditConnectedProvider; connectionId: string }[];
}

/** One row of GET /v1/connectors/connections (the fields this form reads). */
export interface ConnectionOption { id: string; provider: string; accountRef: string; displayName: string | null; status: string }

const isAuditProvider = (p: string): p is AuditConnectedProvider => (AUDIT_CONNECTED_PROVIDERS as readonly string[]).includes(p);

/** Connected accounts the audit can read, grouped by provider in the audit's provider order. Anything not connected is not offered. */
export function pickableConnections(list: readonly ConnectionOption[] | null | undefined): Partial<Record<AuditConnectedProvider, ConnectionOption[]>> {
  const out: Partial<Record<AuditConnectedProvider, ConnectionOption[]>> = {};
  for (const c of list ?? []) {
    if (c.status !== 'connected' || !isAuditProvider(c.provider)) continue;
    (out[c.provider] ??= []).push(c);
  }
  return out;
}

/** Trimmed body; `slots[i]` is the input index of competitors[i], so a server index maps back to its field. */
export function buildJobBody(entitlementId: string, targetUrl: string, fields: readonly string[], locale: 'en' | 'ar', connected: Readonly<Record<string, string>> = {}): { body: JobBody; slots: number[] } {
  const slots: number[] = [];
  const competitors: string[] = [];
  fields.forEach((v, i) => { const s = v.trim(); if (s) { slots.push(i); competitors.push(s); } });
  const connectedAccounts = AUDIT_CONNECTED_PROVIDERS.flatMap((provider) => (connected[provider] ? [{ provider, connectionId: connected[provider]! }] : []));
  return { body: { entitlementId, targetUrl: targetUrl.trim(), locale, ...(competitors.length ? { competitors } : {}), ...(connectedAccounts.length ? { connectedAccounts } : {}) }, slots };
}

export type JobError = { slot: number | 'competitors' | 'website' | 'connected' | 'form'; key: string; params: Record<string, string | number> };

const REASONS = new Set(['invalid_url', 'not_https', 'not_public', 'own_domain']);

export function jobErrorFor(e: unknown, slots: readonly number[], hadConnected = false): JobError {
  if (!(e instanceof BffError)) return { slot: 'form', key: 'shell.error.action', params: {} };
  const code = e.envelope?.error.code;
  const d = (e.envelope?.error.details ?? {}) as { field?: unknown; index?: unknown; reason?: unknown; limit?: unknown; code?: unknown };
  if (d.code === 'connected_accounts_unavailable') return { slot: 'connected', key: 'shell.screens.audit.job.err.connected_unavailable', params: {} };
  if (d.reason === 'website_locked') return { slot: 'website', key: 'shell.screens.audit.job.err.website_locked', params: {} };
  // a 404 with no field is qm-api refusing a connection id that is not this workspace's
  if (e.status === 404 && hadConnected && d.field === undefined) return { slot: 'connected', key: 'shell.screens.audit.job.err.connected_not_found', params: {} };
  if (d.field === 'competitors') {
    if (code === 'feature_disabled') return { slot: 'competitors', key: 'shell.screens.audit.job.err.unavailable', params: {} };
    if (d.reason === 'too_many') return { slot: 'competitors', key: 'shell.screens.audit.job.err.too_many', params: { limit: typeof d.limit === 'number' ? d.limit : 0 } };
    const slot = typeof d.index === 'number' ? slots[d.index] : undefined;
    const key = `shell.screens.audit.job.err.${typeof d.reason === 'string' && REASONS.has(d.reason) ? d.reason : 'invalid_url'}`;
    return { slot: slot ?? 'competitors', key, params: {} };
  }
  if (code === 'validation_failed' || code === 'bad_request') return { slot: 'website', key: 'shell.screens.audit.job.err.website', params: {} };
  if (e.status === 402) return { slot: 'form', key: 'shell.screens.audit.job.err.payment', params: {} };
  if (e.status === 409) return { slot: 'form', key: 'shell.screens.audit.job.err.used', params: {} };
  return { slot: 'form', key: 'shell.error.action', params: {} };
}

const newKey = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `job-${Date.now()}-${Math.random().toString(36).slice(2)}`);

export function AuditJobPage() {
  const { t, locale } = useApp();
  const [params] = useSearchParams();
  const entitlementId = params.get('entitlement') ?? '';
  const limit = competitorLimitFor(params.get('tier'));
  // the connected report of a website order arrives with the order's website (qm-audit locks the grant to it)
  const [website, setWebsite] = useState(() => params.get('website') ?? '');
  const [fields, setFields] = useState<string[]>(() => Array.from({ length: limit }, () => ''));
  const [connected, setConnected] = useState<Record<string, string>>({});
  const connections = useResource<ConnectionOption[]>(entitlementId ? '/v1/connectors/connections' : null);
  const options = pickableConnections(connections.status === 'ready' ? connections.data : null);
  const providers = AUDIT_CONNECTED_PROVIDERS.filter((p) => options[p]?.length);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<false | { id: string | null }>(false);
  const [error, setError] = useState<JobError | null>(null);
  // one key per form: a double submit or a retry after a lost answer creates one job, not two
  const idemKey = useMemo(newKey, []);
  const groupId = useId();

  if (!entitlementId) {
    return (
      <section className="qm-page" aria-labelledby="page-title">
        <Link to="/audit">{t('shell.screens.audit.back')}</Link>
        <Heading level={1} id="page-title">{t('shell.screens.audit.job.title')}</Heading>
        <EmptyState title={t('shell.screens.audit.job.noEntitlement')} />
      </section>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const { body, slots } = buildJobBody(entitlementId, website, fields, locale === 'ar' ? 'ar' : 'en', connected);
    setBusy(true);
    try {
      const created = await bff<{ id?: string } | null>('/v1/growth-audit/jobs', { method: 'POST', body: JSON.stringify(body), headers: { 'idempotency-key': idemKey } });
      setDone({ id: typeof created?.id === 'string' ? created.id : null });
    } catch (err) {
      setError(jobErrorFor(err, slots, Object.values(connected).some(Boolean)));
    } finally {
      setBusy(false);
    }
  };

  const msg = (slot: JobError['slot']) => (error && error.slot === slot ? t(error.key, error.params) : null);
  const groupError = msg('competitors');

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <Link to="/audit">{t('shell.screens.audit.back')}</Link>
      <Heading level={1} id="page-title">{t('shell.screens.audit.job.title')}</Heading>
      {done ? (
        <Panel>
          <Text role="status">{t('shell.screens.audit.job.started')}</Text>
          <Link to={done.id ? `/audit/${encodeURIComponent(done.id)}` : '/audit'}>{done.id ? t('shell.p1.list.view') : t('shell.screens.audit.back')}</Link>
        </Panel>
      ) : (
        <Panel>
          <Text tone="muted">{t('shell.screens.audit.job.intro')}</Text>
          <form onSubmit={submit} noValidate>
            <Field label={t('shell.screens.audit.job.website')} hint={t('shell.screens.audit.job.websiteHint')} error={msg('website')}>
              {({ id, describedBy, invalid }) => (
                <TextInput id={id} name="targetUrl" type="url" inputMode="url" autoComplete="url" dir="ltr" required value={website}
                  aria-describedby={describedBy} aria-invalid={invalid || undefined} onChange={(ev) => setWebsite(ev.target.value)} />
              )}
            </Field>
            <div role="group" aria-labelledby={`${groupId}-label`} aria-describedby={`${groupId}-hint${groupError ? ` ${groupId}-err` : ''}`}>
              <Heading level={2} id={`${groupId}-label`}>{t('shell.screens.audit.job.competitors')}</Heading>
              <Text tone="muted" id={`${groupId}-hint`}>{t('shell.screens.audit.job.competitorsHint', { limit })}</Text>
              {groupError ? <p className="qm-field__error" id={`${groupId}-err`} role="alert">{groupError}</p> : null}
              {fields.map((v, i) => (
                <Field key={i} label={t('shell.screens.audit.job.competitor', { n: i + 1 })} error={msg(i)}>
                  {({ id, describedBy, invalid }) => (
                    <TextInput id={id} name="competitor" type="text" inputMode="url" autoComplete="off" dir="ltr" value={v}
                      aria-describedby={describedBy} aria-invalid={invalid || undefined}
                      onChange={(ev) => { const next = [...fields]; next[i] = ev.target.value; setFields(next); }} />
                  )}
                </Field>
              ))}
            </div>
            <div role="group" aria-labelledby={`${groupId}-conn-label`}>
              <Heading level={2} id={`${groupId}-conn-label`}>{t('shell.screens.audit.job.connected')}</Heading>
              <Text tone="muted">{t('shell.screens.audit.job.connectedHint')}</Text>
              {connections.status === 'loading' ? <Loading label={t('common.loading')} /> : null}
              {connections.status === 'error' ? <Text tone="muted">{t('shell.screens.audit.job.connectedLoadFailed')}</Text> : null}
              {connections.status === 'ready' && !providers.length ? (
                <Text tone="muted">{t('shell.screens.audit.job.connectedEmpty')} <Link to="/integrations">{t('shell.screens.audit.job.connectedLink')}</Link></Text>
              ) : null}
              {providers.map((p) => (
                <Field key={p} label={t(`shell.screens.audit.job.provider.${p}`)}>
                  {({ id, describedBy }) => (
                    <Select id={id} name={`connected-${p}`} aria-describedby={describedBy} value={connected[p] ?? ''}
                      onChange={(ev) => setConnected({ ...connected, [p]: ev.target.value })}>
                      <option value="">{t('shell.screens.audit.job.connectedNone')}</option>
                      {options[p]!.map((c) => <option key={c.id} value={c.id}>{c.displayName ?? c.accountRef}</option>)}
                    </Select>
                  )}
                </Field>
              ))}
              {msg('connected') ? <p className="qm-field__error" role="alert">{msg('connected')}</p> : null}
            </div>
            {msg('form') ? <ErrorState message={msg('form')!} /> : null}
            <Button variant="primary" type="submit" disabled={busy}>{t('shell.screens.audit.job.submit')}</Button>
          </form>
        </Panel>
      )}
    </section>
  );
}
